import { bindActionCreators } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import { keyBy } from 'lodash-es';

import { BookingStatus, IBookingSimple, PresenceType } from '~/Models/DTO/Booking.models';
import {
  requestBookingListForEvent,
  requestClearBookingList,
  requestCustomerTags,
  requestUpdateBookings,
  requestUpdateMultipleBookingPresence,
  requestUpdateSingleBooking,
} from '~/store';
import { useLanguage, useNavigation, useProvider } from '~/Hooks';
import { markBookingAsAction, sortBookingsBy, updateBookingStatus } from './Bookinglist.helpers';
import { requestSingleEvent, selectEventById } from '~/store/events';
import { selectBookingsList, selectIsCheckInOpen } from '~/store/bookings';
import { selectLoading } from '~/store/app';
import { REQUEST_ACTIONS } from '~/Constants/Actions.constants';

export const allTabActions = (
  markAsAction: ReturnType<typeof markBookingAsAction>,
  cancelAction: ReturnType<typeof updateBookingStatus>,
) => [
  {
    label: 'app.bookinglist.markpresent',
    action: (bookings: IBookingSimple[]) => markAsAction(PresenceType.PRESENT, bookings),
  },
  {
    label: 'app.bookinglist.markabsent',
    action: (bookings: IBookingSimple[]) => markAsAction(PresenceType.ABSENT, bookings),
  },
  {
    label: 'app.booking.cancel',
    action: (bookings: IBookingSimple[]) => cancelAction(BookingStatus.CANCELLED, bookings),
  },
];

export const cancelTabActions = (bookAction: ReturnType<typeof updateBookingStatus>) => [
  {
    label: 'app.booking.rebook',
    action: (bookings: IBookingSimple[]) => bookAction(BookingStatus.BOOKED, bookings),
  },
];

export const useBookingList = () => {
  // state
  const [sortBy, setSortBy] = useState('datedesc');
  const [onlyShow, setOnlyShow] = useState(BookingStatus.BOOKED);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedBookings, setSelectedBookings] = useState([] as string[]);

  // hooks
  const dispatch = useDispatch();
  const { getParams } = useNavigation();
  const { id } = getParams();
  const { t } = useLanguage();
  const { currentProvider } = useProvider();

  // actions
  const loadBookings = bindActionCreators(requestBookingListForEvent, dispatch);
  const bookingsPresenceUpdate = bindActionCreators(requestUpdateMultipleBookingPresence, dispatch);
  const bookingsUpdate = bindActionCreators(requestUpdateBookings, dispatch);
  const bookingsUpdateSingle = bindActionCreators(requestUpdateSingleBooking, dispatch);
  const loadEvent = bindActionCreators(requestSingleEvent, dispatch);
  const bookinglistClear = bindActionCreators(requestClearBookingList, dispatch);
  const getTags = bindActionCreators(requestCustomerTags, dispatch);

  // selectors
  const selectedEvent = useSelector(selectEventById(id || ''));
  const isCheckInOpen = useSelector(selectIsCheckInOpen(id || ''));
  const bookingsHistory = useSelector(selectBookingsList);
  const bookings = useMemo(
    () =>
      Object.values(
        keyBy(
          bookingsHistory
            ?.slice()
            .sort(
              (first, second) => Date.parse(first.createdDate) - Date.parse(second.createdDate),
            ),
          'customer.id',
        ),
      ),
    [bookingsHistory],
  ) as IBookingSimple[];
  const isBookingsLoading = useSelector(selectLoading(REQUEST_ACTIONS.BOOKING_LIST));
  const showableBookings = bookings.filter((booking) => booking.status === onlyShow);
  const bookingsCount = bookings.filter((booking) => booking.status === BookingStatus.BOOKED)
    .length;
  const cancelledBookings = bookings.filter(
    (booking) => booking.status === BookingStatus.CANCELLED,
  );
  const waitingBookings = bookings.filter(
    (booking) => booking.status === BookingStatus.WAITING_LIST,
  );
  const sortedBookings = sortBookingsBy(showableBookings, sortBy);

  const markBookingAction = markBookingAsAction(bookingsPresenceUpdate);
  const updateBookingStatusAction = updateBookingStatus(bookingsUpdate);

  const availableActions =
    onlyShow === BookingStatus.BOOKED
      ? allTabActions(markBookingAction, updateBookingStatusAction)
      : cancelTabActions(updateBookingStatusAction);

  const handleMarkSingleBookingAs = (booking: IBookingSimple, markAs: PresenceType) => {
    markBookingAction(markAs, [
      {
        bookingId: booking.id,
        presenceType: markAs,
        origin: 'NEOPRO',
        force: true,
      },
    ]);
  };

  const loadEventBookings = () => {
    if (currentProvider) {
      loadBookings({
        domain: currentProvider.uuid,
        eventId: selectedEvent.id,
      });
    }
  };

  useEffect(() => {
    bookinglistClear();
    if (!selectedEvent) {
      loadEvent(id || '');
    } else {
      loadEventBookings();
    }
  }, []);

  useEffect(() => {
    if (sortedBookings.length) {
      getTags(
        sortedBookings
          .map((booking) => booking.customer?.id || 0)
          .filter((customerId) => customerId !== 0),
        currentProvider?.uuid || '',
      );
    }
  }, [sortedBookings]);

  const handleSelectAll = () => {
    if (selectedBookings.length === showableBookings.length) {
      setSelectedBookings([]);
    } else {
      setSelectedBookings(showableBookings.map((booking) => booking.uuid));
    }
  };

  const handleActionSelection = (action: any) => {
    if (!action) {
      return;
    }
    const actionableBookings = sortedBookings.filter((booking) =>
      selectedBookings.includes(booking.uuid),
    );
    action.action(
      actionableBookings.map((booking) => ({
        bookingId: booking.id,
        bookingStatus: booking.bookingStatus,
        origin: 'NEO-PRO',
        force: true,
      })),
    );
    setSelectMode(false);
    setSelectedBookings([]);
  };

  const handleTabSelection = (value: string) => {
    if (value !== onlyShow) {
      setSelectedBookings([]);
    }
    setOnlyShow(value as BookingStatus);
    setSelectMode(false);
  };

  const handleSelectModeChange = () => {
    setSelectMode(!selectMode);
  };

  const handleSelectSingle = (uuid: string) => {
    if (!selectedBookings.includes(uuid)) {
      setSelectedBookings([...selectedBookings, uuid]);
    } else {
      setSelectedBookings([...selectedBookings.filter((id) => id !== uuid)]);
    }
  };

  const handleUpdateBooking = (booking: IBookingSimple, status: BookingStatus) => {
    bookingsUpdateSingle({
      bookingId: booking.id,
      bookingStatus: status,
      presenceType: booking.presenceType,
      refund: booking.cancelledWithRefund,
      origin: 'NEO-PRO',
      force: true,
    });
  };

  return {
    bookingsCount,
    isBookingsLoading,
    cancelledBookings: cancelledBookings.length,
    waitingBookings: waitingBookings.length,
    handleActionSelection,
    handleSelectModeChange,
    handleMarkSingleBookingAs,
    handleUpdateBooking,
    selectedBookings,
    sortedBookings,
    availableActions,
    isCheckInOpen,
    selectAll: handleSelectAll,
    selectSingle: handleSelectSingle,
    selectMode,
    setSortBy,
    sortBy,
    onlyShow,
    handleTabSelection,
    loadEventBookings,
    t,
  };
};
